import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Building2, Plus, Save, Edit, Trash2, Users, MapPin, Phone, Mail, ChevronDown, ChevronUp, RefreshCw, Download, Upload, Link2, Navigation, Lock } from 'lucide-react';

const enconOrange = '#ee7203';

const BranchDirectory = ({ user }) => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBranch, setEditingBranch] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [expandedBranch, setExpandedBranch] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [refreshingCodes, setRefreshingCodes] = useState(false);
  const [fixingBranchData, setFixingBranchData] = useState(false);
  const [dedupingGoodsIn, setDedupingGoodsIn] = useState(false);
  const fileInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    nickname: '',
    branchCode: '',
    address1: '',
    address2: '',
    city: '',
    postcode: '',
    phone: '',
    email: '',
    default_contact: '',
    contacts: [],
    latitude: '',
    longitude: '',
    source_url: '',
    is_encon_insulation: false,
    is_nevill_long: false
  });

  useEffect(() => {
    loadBranches();
  }, []);

  const loadBranches = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API}/branch-directory`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setBranches(response.data || []);
    } catch (error) {
      console.error('Error loading branch directory:', error);
      toast.error('Failed to load branch directory');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    if (!window.confirm('This will populate the branch directory with default branch data. Continue?')) {
      return;
    }
    
    setSeeding(true);
    try {
      const response = await api.post(`${API}/branch-directory/seed`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.seeded) {
        toast.success(`Added ${response.data.count} branches to directory`);
        loadBranches();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error('Error seeding branch directory:', error);
      // Show more detailed error message
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to seed branch directory';
      toast.error(errorMsg);
    } finally {
      setSeeding(false);
    }
  };

  const handleRefreshTradingPointCodes = async () => {
    if (!window.confirm('This will populate/refresh Trading Point codes (e.g. A10, C52) on every branch, rename Thetford, and add Belfast, Wrexham, Central Distribution and the Construction Products entries. Safe to run more than once. Continue?')) {
      return;
    }

    setRefreshingCodes(true);
    try {
      const response = await api.post(`${API}/automations/refresh-branch-trading-point-codes`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const r = response.data || {};
      const parts = [];
      if (r.updated?.length) parts.push(`${r.updated.length} code(s) set`);
      if (r.renamed?.length) parts.push(`${r.renamed.length} renamed`);
      if (r.inserted?.length) parts.push(`${r.inserted.length} new branch(es)`);
      if (r.cp_inserted?.length) parts.push(`${r.cp_inserted.length} CP entries`);
      if (r.errors?.length) {
        toast.error(`Refresh completed with ${r.errors.length} error(s): ${r.errors[0]}${r.errors.length > 1 ? ' (+more, see console)' : ''}`);
        console.error('Trading Point refresh errors:', r.errors);
      } else if (parts.length === 0) {
        toast.info('Trading Point codes already up to date — nothing to change');
      } else {
        toast.success(`Trading Point codes refreshed: ${parts.join(', ')}`);
      }
      loadBranches();
    } catch (error) {
      console.error('Error refreshing trading point codes:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to refresh trading point codes';
      toast.error(errorMsg);
    } finally {
      setRefreshingCodes(false);
    }
  };

  const handleFixBranchDirectory = async () => {
    if (!window.confirm('This will:\n\n1. Set the correct, final address for Birmingham, West Bromwich, Maidstone, Plymouth, Cardiff, Glasgow, Belfast and their Construction Products entries — this data is now fixed and permanent, nothing auto-refreshes it any more\n2. Remove the Newmarket and Washington entries (merged into East Anglia/Thetford and North East/Newcastle)\n3. Re-check every auto-created Goods In entry against the corrected data and move any that were filed under the wrong branch\n\nSafe to run more than once. Continue?')) {
      return;
    }

    setFixingBranchData(true);
    try {
      const response = await api.post(`${API}/automations/fix-branch-directory-and-goods-in`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const r = response.data || {};
      const parts = [];
      if (r.postcodes_fixed?.length) parts.push(`${r.postcodes_fixed.length} postcode(s) corrected`);
      if (r.branches_removed?.length) parts.push(`${r.branches_removed.length} branch(es) removed`);
      parts.push(`${r.goods_in_moved_count || 0} of ${r.goods_in_checked || 0} Goods In entries re-filed`);
      toast.success(`Branch directory fixed: ${parts.join(', ')}`);
      console.log('Branch directory fix result:', r);
      loadBranches();
    } catch (error) {
      console.error('Error fixing branch directory:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to fix branch directory';
      toast.error(errorMsg);
    } finally {
      setFixingBranchData(false);
    }
  };

  const handleDedupeGoodsIn = async () => {
    if (!window.confirm('This will remove manually-added Goods In entries that duplicate an automated Purchase Order email entry for the same order number — e.g. a bare "KNAUF" note next to the full "KNAUF UK GMBH" PO record.\n\nThe automated entry is always kept. If the manual one was marked complete and the automated one wasn\'t, that completion is carried over first so nothing is lost.\n\nOrders with no automated entry, or with more than one, are left untouched for you to check by hand. Safe to run more than once. Continue?')) {
      return;
    }

    setDedupingGoodsIn(true);
    try {
      const response = await api.post(`${API}/automations/dedupe-goods-in-manual-entries`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const r = response.data || {};
      const parts = [`${r.manual_entries_removed || 0} duplicate manual entr${(r.manual_entries_removed === 1) ? 'y' : 'ies'} removed`];
      if (r.statuses_carried_forward) parts.push(`${r.statuses_carried_forward} completion(s) carried forward`);
      if (r.skipped_no_automated_entry) parts.push(`${r.skipped_no_automated_entry} order(s) skipped (no automated entry)`);
      if (r.skipped_multiple_automated_entries) parts.push(`${r.skipped_multiple_automated_entries} order(s) skipped (multiple automated entries)`);
      toast.success(`Goods In cleaned up: ${parts.join(', ')}`);
      console.log('Goods In dedupe result:', r);
    } catch (error) {
      console.error('Error removing duplicate Goods In entries:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to remove duplicate entries';
      toast.error(errorMsg);
    } finally {
      setDedupingGoodsIn(false);
    }
  };

  // Export branches to JSON file
  const handleExport = () => {
    if (branches.length === 0) {
      toast.error('No branches to export');
      return;
    }
    
    // Clean up data for export (remove MongoDB-specific fields)
    const exportData = branches.map(b => ({
      id: b.id,
      name: b.name,
      nickname: b.nickname || '',
      branchCode: b.branchCode || null,
      address1: b.address1 || '',
      address2: b.address2 || '',
      city: b.city || '',
      postcode: b.postcode || '',
      phone: b.phone || '',
      email: b.email || '',
      default_contact: b.default_contact || '',
      contacts: b.contacts || []
    }));
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `branch_directory_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${branches.length} branches`);
  };

  // Import branches from JSON file
  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const text = await file.text();
      const importedBranches = JSON.parse(text);
      
      if (!Array.isArray(importedBranches)) {
        throw new Error('Invalid format - expected an array of branches');
      }
      
      // Import each branch
      let added = 0;
      let updated = 0;
      let errors = 0;
      
      for (const branch of importedBranches) {
        if (!branch.id || !branch.name) {
          errors++;
          continue;
        }
        
        try {
          // Check if branch exists
          const existing = branches.find(b => b.id === branch.id);
          
          if (existing) {
            // Update existing
            await api.put(`${API}/branch-directory/${branch.id}`, branch, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            updated++;
          } else {
            // Create new
            await api.post(`${API}/branch-directory`, branch, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            added++;
          }
        } catch (err) {
          console.error(`Error importing branch ${branch.id}:`, err);
          errors++;
        }
      }
      
      toast.success(`Import complete: ${added} added, ${updated} updated${errors > 0 ? `, ${errors} errors` : ''}`);
      loadBranches();
    } catch (error) {
      console.error('Error importing branches:', error);
      toast.error('Failed to import: ' + (error.message || 'Invalid file format'));
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openEditModal = (branch = null) => {
    if (branch) {
      setEditingBranch(branch);
      setFormData({
        id: branch.id || '',
        name: branch.name || '',
        nickname: branch.nickname || '',
        branchCode: branch.branchCode || '',
        address1: branch.address1 || '',
        address2: branch.address2 || '',
        city: branch.city || '',
        postcode: branch.postcode || '',
        phone: branch.phone || '',
        email: branch.email || '',
        default_contact: branch.default_contact || '',
        contacts: branch.contacts || [],
        latitude: branch.latitude ?? '',
        longitude: branch.longitude ?? '',
        source_url: branch.source_url || '',
        is_encon_insulation: !!branch.is_encon_insulation,
        is_nevill_long: !!branch.is_nevill_long
      });
    } else {
      setEditingBranch(null);
      setFormData({
        id: '',
        name: '',
        nickname: '',
        branchCode: '',
        address1: '',
        address2: '',
        city: '',
        postcode: '',
        phone: '',
        email: '',
        default_contact: '',
        contacts: [],
        latitude: '',
        longitude: '',
        source_url: '',
        is_encon_insulation: false,
        is_nevill_long: false
      });
    }
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Branch name is required');
      return;
    }

    const payload = {
      ...formData,
      latitude: formData.latitude === '' ? null : parseFloat(formData.latitude),
      longitude: formData.longitude === '' ? null : parseFloat(formData.longitude),
    };

    setSaving(true);
    try {
      if (editingBranch) {
        // Update existing
        await api.put(`${API}/branch-directory/${editingBranch.id}`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Branch updated successfully');
      } else {
        // Create new
        const newId = formData.id || formData.name.toLowerCase().replace(/\s+/g, '-');
        await api.post(`${API}/branch-directory`, { ...payload, id: newId }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Branch created successfully');
      }
      setShowEditModal(false);
      loadBranches();
    } catch (error) {
      console.error('Error saving branch:', error);
      toast.error(error.response?.data?.detail || 'Failed to save branch');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (branchId) => {
    if (!window.confirm('Are you sure you want to delete this branch from the directory?')) {
      return;
    }

    try {
      await api.delete(`${API}/branch-directory/${branchId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Branch deleted');
      loadBranches();
    } catch (error) {
      console.error('Error deleting branch:', error);
      toast.error('Failed to delete branch');
    }
  };

  const addContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, { name: '', role: '', email: '', phone: '' }]
    }));
  };

  const updateContact = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  const removeContact = (index) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index)
    }));
  };

  const isSuperAdmin = user?.role === 'super_admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin" style={{ color: enconOrange }} />
        <span className="ml-2">Loading branch directory...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: enconOrange }}>
            <MapPin className="w-6 h-6" />
            Branch Directory
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage branch addresses and contacts for stock transfers. This data is used throughout the system.
          </p>
        </div>
        {isSuperAdmin && (
          <div className="flex gap-2 flex-wrap">
            {/* Hidden file input for import */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImport}
              accept=".json"
              className="hidden"
            />
            
            {branches.length === 0 && (
              <Button
                onClick={handleSeedData}
                disabled={seeding}
                variant="outline"
                className="border-orange-300 text-orange-600 hover:bg-orange-50"
              >
                <Download className="w-4 h-4 mr-2" />
                {seeding ? 'Loading...' : 'Load Default Branches'}
              </Button>
            )}

            <Button
              onClick={handleRefreshTradingPointCodes}
              disabled={refreshingCodes}
              variant="outline"
              className="border-orange-300 text-orange-600 hover:bg-orange-50"
              title="Populate/refresh Trading Point codes (A10, C52, etc.) on every branch, and add any missing branches or Construction Products entries"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshingCodes ? 'animate-spin' : ''}`} />
              {refreshingCodes ? 'Refreshing...' : 'Refresh Trading Point Codes'}
            </Button>

            <Button
              onClick={handleFixBranchDirectory}
              disabled={fixingBranchData}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              title="Set the final correct address for the affected branches, remove merged Newmarket/Washington entries, and re-file any Goods In entries that were auto-filed under the wrong branch as a result"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${fixingBranchData ? 'animate-spin' : ''}`} />
              {fixingBranchData ? 'Fixing...' : 'Fix Branch Data & Re-file Goods In'}
            </Button>

            <Button
              onClick={handleDedupeGoodsIn}
              disabled={dedupingGoodsIn}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              title="Remove manually-added Goods In entries that duplicate an automated Purchase Order email entry for the same order number, keeping the automated one"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${dedupingGoodsIn ? 'animate-spin' : ''}`} />
              {dedupingGoodsIn ? 'Removing...' : 'Remove Duplicate Goods In Entries'}
            </Button>
            
            {branches.length > 0 && (
              <Button
                onClick={handleExport}
                variant="outline"
                className="border-green-300 text-green-600 hover:bg-green-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Export JSON
              </Button>
            )}
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              variant="outline"
              className="border-blue-300 text-blue-600 hover:bg-blue-50"
            >
              <Upload className="w-4 h-4 mr-2" />
              {importing ? 'Importing...' : 'Import JSON'}
            </Button>
            
            <Button
              onClick={() => openEditModal()}
              style={{ backgroundColor: enconOrange }}
              className="text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Branch
            </Button>
          </div>
        )}
      </div>

      {/* Branch List */}
      {branches.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-12 text-center">
            <MapPin className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600">No Branches in Directory</h3>
            <p className="text-sm text-gray-500 mt-2">
              {isSuperAdmin 
                ? 'Click "Load Default Branches" to populate with Encon branch data, or add branches manually.'
                : 'Contact a super admin to set up the branch directory.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {branches.map(branch => {
            const isExpanded = expandedBranch === branch.id;
            
            return (
              <Card key={branch.id} className={`transition-all ${isExpanded ? 'ring-2 ring-orange-300' : ''}`}>
                <CardHeader className="pb-2">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedBranch(isExpanded ? null : branch.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5" style={{ color: enconOrange }} />
                      <div>
                        <CardTitle className="text-base">{branch.name}</CardTitle>
                        <div className="flex items-center gap-2 mt-0.5">
                          {branch.nickname && (
                            <span className="text-xs text-gray-500">({branch.nickname})</span>
                          )}
                          {branch.branchCode && (
                            <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded font-mono">
                              {branch.branchCode}
                            </span>
                          )}
                          {branch.default_contact && (
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {branch.default_contact}
                            </span>
                          )}
                          {branch.address_verified && (
                            <span
                              className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded flex items-center gap-1"
                              title="Address manually verified — this is fixed, permanent data"
                              data-testid={`branch-verified-badge-${branch.id}`}
                            >
                              <Lock className="w-3 h-3" />
                              Verified
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isSuperAdmin && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); openEditModal(branch); }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); handleDelete(branch.id); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                  </div>
                </CardHeader>
                
                {isExpanded && (
                  <CardContent className="pt-2 border-t">
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Address */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                          <MapPin className="w-4 h-4" /> Address
                        </h4>
                        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                          {branch.address1 && <p>{branch.address1}</p>}
                          {branch.address2 && <p>{branch.address2}</p>}
                          {(branch.city || branch.postcode) && (
                            <p>{[branch.city, branch.postcode].filter(Boolean).join(', ')}</p>
                          )}
                          {!branch.address1 && !branch.city && (
                            <p className="text-gray-400 italic">No address on file</p>
                          )}
                        </div>
                        <div className="mt-2 space-y-1">
                          {branch.phone && (
                            <p className="text-sm text-gray-600 flex items-center gap-2">
                              <Phone className="w-3 h-3" /> {branch.phone}
                            </p>
                          )}
                          {branch.email && (
                            <p className="text-sm text-gray-600 flex items-center gap-2">
                              <Mail className="w-3 h-3" /> {branch.email}
                            </p>
                          )}
                        </div>

                        {/* Map & brand info */}
                        <div className="mt-3 pt-3 border-t">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                            <Navigation className="w-4 h-4" /> Map & Brand
                          </h4>
                          <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded space-y-1">
                            {typeof branch.latitude === 'number' && typeof branch.longitude === 'number' ? (
                              <p data-testid={`branch-coords-${branch.id}`}>Coords: {branch.latitude.toFixed(4)}, {branch.longitude.toFixed(4)}</p>
                            ) : (
                              <p className="text-gray-400 italic">No coordinates set (won't show on live map)</p>
                            )}
                            <div className="flex items-center gap-2 flex-wrap">
                              {branch.is_encon_insulation && (
                                <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">Encon Insulation</span>
                              )}
                              {branch.is_nevill_long && (
                                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Nevill Long</span>
                              )}
                            </div>
                            {branch.source_url && (
                              <p className="flex items-center gap-1 text-xs truncate">
                                <Link2 className="w-3 h-3 flex-shrink-0" />
                                <a href={branch.source_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate" data-testid={`branch-source-url-${branch.id}`}>{branch.source_url}</a>
                              </p>
                            )}
                            {branch.address_verified ? (
                              <p className="text-xs text-green-600 flex items-center gap-1">
                                <Lock className="w-3 h-3" /> Address verified — fixed, permanent data
                              </p>
                            ) : branch.last_refreshed && (
                              <p className="text-xs text-gray-400">Last refreshed: {new Date(branch.last_refreshed).toLocaleString()} (historical — the auto-refresh job no longer exists)</p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Contacts */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                          <Users className="w-4 h-4" /> Contacts
                        </h4>
                        {branch.contacts?.length > 0 ? (
                          <div className="space-y-2">
                            {branch.contacts.map((contact, idx) => (
                              <div key={idx} className="text-sm bg-gray-50 p-2 rounded">
                                <p className="font-medium">{contact.name}</p>
                                {contact.role && <p className="text-gray-500 text-xs">{contact.role}</p>}
                                {contact.email && <p className="text-gray-500 text-xs">{contact.email}</p>}
                                {contact.phone && <p className="text-gray-500 text-xs">{contact.phone}</p>}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic bg-gray-50 p-3 rounded">No contacts added</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit/Create Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: enconOrange }}>
              {editingBranch ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingBranch ? 'Edit Branch' : 'Add New Branch'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Branch Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Encon Birmingham"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Nickname</label>
                <Input
                  value={formData.nickname}
                  onChange={(e) => setFormData(prev => ({ ...prev, nickname: e.target.value }))}
                  placeholder="e.g., Birmingham"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Branch Code</label>
                <Input
                  value={formData.branchCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, branchCode: e.target.value.toUpperCase() }))}
                  placeholder="e.g., C30"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">System branch code if applicable</p>
              </div>
              <div>
                <label className="text-sm font-medium">Default Transfer Contact</label>
                <Input
                  value={formData.default_contact}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_contact: e.target.value }))}
                  placeholder="e.g., Claire"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Address */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" style={{ color: enconOrange }} />
                Address
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Address Line 1</label>
                  <Input
                    value={formData.address1}
                    onChange={(e) => setFormData(prev => ({ ...prev, address1: e.target.value }))}
                    placeholder="Street address"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Address Line 2</label>
                  <Input
                    value={formData.address2}
                    onChange={(e) => setFormData(prev => ({ ...prev, address2: e.target.value }))}
                    placeholder="Area, district"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-3">
                <div>
                  <label className="text-sm font-medium">City</label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="City"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Postcode</label>
                  <Input
                    value={formData.postcode}
                    onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value.toUpperCase() }))}
                    placeholder="B6 7AF"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Phone</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="0121 356 0606"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="text-sm font-medium">Email</label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="branch@encon.co.uk"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Contacts */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4" style={{ color: enconOrange }} />
                  Contacts
                </h4>
                <Button size="sm" variant="outline" onClick={addContact}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add Contact
                </Button>
              </div>
              
              {formData.contacts.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded">
                  No contacts added. Click "Add Contact" to add branch contacts.
                </p>
              ) : (
                <div className="space-y-3">
                  {formData.contacts.map((contact, idx) => (
                    <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-medium text-gray-500">Contact {idx + 1}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => removeContact(idx)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          value={contact.name}
                          onChange={(e) => updateContact(idx, 'name', e.target.value)}
                          placeholder="Name"
                          className="bg-white"
                        />
                        <Input
                          value={contact.role}
                          onChange={(e) => updateContact(idx, 'role', e.target.value)}
                          placeholder="Job Role"
                          className="bg-white"
                        />
                        <Input
                          value={contact.email}
                          onChange={(e) => updateContact(idx, 'email', e.target.value)}
                          placeholder="Email"
                          className="bg-white"
                        />
                        <Input
                          value={contact.phone}
                          onChange={(e) => updateContact(idx, 'phone', e.target.value)}
                          placeholder="Phone"
                          className="bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Map & Brand */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Navigation className="w-4 h-4" style={{ color: enconOrange }} />
                Map Marker & Brand
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Latitude</label>
                  <Input
                    data-testid="branch-form-latitude"
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                    placeholder="52.5223"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Longitude</label>
                  <Input
                    data-testid="branch-form-longitude"
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                    placeholder="-1.8852"
                    className="mt-1"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">Used to plot this branch as a marker on the live tracking map. Leave blank to hide it from the map.</p>
              <div className="mt-3">
                <label className="text-sm font-medium">Source URL</label>
                <Input
                  data-testid="branch-form-source-url"
                  value={formData.source_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, source_url: e.target.value }))}
                  placeholder="https://www.encon.co.uk/branches/..."
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">This branch's own page on encon.co.uk, kept for reference only — nothing reads this automatically any more.</p>
              </div>
              <div className="flex items-center gap-6 mt-3">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    data-testid="branch-form-is-encon-insulation"
                    type="checkbox"
                    checked={formData.is_encon_insulation}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_encon_insulation: e.target.checked }))}
                  />
                  Encon Insulation
                </label>
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    data-testid="branch-form-is-nevill-long"
                    type="checkbox"
                    checked={formData.is_nevill_long}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_nevill_long: e.target.checked }))}
                  />
                  Nevill Long
                </label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              style={{ backgroundColor: enconOrange }}
              className="text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Branch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchDirectory;
